import { PageMainContainer } from "@amzn/it-shop-shared-components";
import { Breadcrumbs, CrumbProps } from "@amzn/stencil-react-components/breadcrumbs";
import { Button, ButtonVariant } from "@amzn/stencil-react-components/button";
import { Col, Row, View } from "@amzn/stencil-react-components/layout";
import { MessageBanner, MessageBannerType } from "@amzn/stencil-react-components/message-banner";
import {
  ProgressTracker,
  ProgressTrackerStep,
  ProgressTrackerStepStatus,
} from "@amzn/stencil-react-components/progress-tracker";
import { ScreenReaderOnly } from "@amzn/stencil-react-components/screen-reader-only";
import { Spinner, SpinnerSize } from "@amzn/stencil-react-components/spinner";
import React, { FC, ReactNode } from "react";

import { DiscardProgressModal } from "@/common/components/DiscardProgressModal/discard-progress-modal";
import { useCrumbClick } from "@/common/hooks/use-crumb-click";

const FORM_COLUMN_MAX_WIDTH = 866;
export const FLOW_ERROR_ID = "flow-step-error";

// Stencil's ProgressTracker reserves 12px below its labels; pulling that back
// makes the gap under the tracker match the design frame.
const TRACKER_TRAILING_SPACE = 12;

interface FlowShellProps {
  crumbs: CrumbProps[];
  heading: ReactNode;
  headingVisibility: "visible" | "sr-only";
  stepLabels: string[];
  progressLabel: string;
  currentStep: number;
  onStepSelected: (step: number) => void;
  onBack?: () => void;
  onContinue: () => void;
  continueLabel: string;
  continueDisabled?: boolean;
  continueBusy?: boolean;
  backLabel: string;
  errorMessage?: string;
  children: ReactNode;
}

const stepStatus = (index: number, currentStep: number): ProgressTrackerStepStatus => {
  if (index < currentStep) {
    return ProgressTrackerStepStatus.Complete;
  }
  if (index === currentStep) {
    return ProgressTrackerStepStatus.Current;
  }
  return ProgressTrackerStepStatus.Disabled;
};

export const FlowShell: FC<FlowShellProps> = ({
  crumbs,
  heading,
  headingVisibility,
  stepLabels,
  progressLabel,
  currentStep,
  onStepSelected,
  onBack,
  onContinue,
  continueLabel,
  continueDisabled,
  continueBusy,
  backLabel,
  errorMessage,
  children,
}) => {
  const onCrumbClick = useCrumbClick();

  return (
    <PageMainContainer paddingTop="S300" paddingBottom="S500">
      <Col gridGap="S400" width="100%">
        <Breadcrumbs crumbs={crumbs} onCrumbClick={onCrumbClick} />

        {headingVisibility === "sr-only" ? (
          <ScreenReaderOnly>
            <h1>{heading}</h1>
          </ScreenReaderOnly>
        ) : (
          <h1>{heading}</h1>
        )}

        <Row justifyContent="center" width="100%">
          <Col gridGap="S400" width="100%" maxWidth={FORM_COLUMN_MAX_WIDTH}>
            <View width="100%" style={{ marginBottom: -TRACKER_TRAILING_SPACE }}>
              <ProgressTracker
                selectedStep={currentStep}
                onStepSelected={onStepSelected}
                aria-label={progressLabel}
              >
                {stepLabels.map((label, index) => (
                  <ProgressTrackerStep
                    key={label}
                    labelText={label}
                    status={stepStatus(index, currentStep)}
                  />
                ))}
              </ProgressTracker>
            </View>

            <Col gridGap="S300" width="100%">
              {errorMessage && (
                <View width="100%" role="alert" id={FLOW_ERROR_ID} data-testid="flow-error">
                  <MessageBanner type={MessageBannerType.Error} isDismissible={false}>
                    {errorMessage}
                  </MessageBanner>
                </View>
              )}

              {children}
            </Col>

            <Row width="100%" justifyContent="space-between" alignItems="center">
              {onBack ? (
                <Button variant={ButtonVariant.Secondary} onClick={onBack} data-testid="flow-back">
                  {backLabel}
                </Button>
              ) : (
                <View />
              )}
              <Button
                variant={ButtonVariant.Primary}
                onClick={onContinue}
                disabled={continueDisabled || continueBusy}
                aria-busy={continueBusy ? true : undefined}
                aria-describedby={errorMessage ? FLOW_ERROR_ID : undefined}
                icon={continueBusy ? <Spinner size={SpinnerSize.Small} showText={false} /> : undefined}
                data-testid="flow-continue"
              >
                {continueLabel}
              </Button>
            </Row>
          </Col>
        </Row>
      </Col>
      <DiscardProgressModal />
    </PageMainContainer>
  );
};
